import { ComponentRef } from '@angular/core';
import { DataObject } from '@shared/models/data-object.model';

export const StateMemberSymbol = Symbol('_stateMembers');
export const SelectionMemberSymbol = Symbol('_selectionMembers');
/**
 * Property decorator to mark a @StateMember field as representing a row selection.
 * Fields decorated with @SelectionMember are cleared (reset to their initial value)
 * when the wizard slot is cleared, while other @StateMember fields (e.g. filters) are preserved.
 */
export function SelectionMember(target: Object, propertyKey: string) {
  if (!target.hasOwnProperty(SelectionMemberSymbol)) {
    Object.defineProperty(target, SelectionMemberSymbol, {
      enumerable: true,
      configurable: true,
      writable: true,
    });
    target[SelectionMemberSymbol] = {};
  }
  if (!target[SelectionMemberSymbol][propertyKey]) {
    target[SelectionMemberSymbol] = { ...target[SelectionMemberSymbol], [propertyKey]: true };
  }
}

export function StateMember(target: Object, propertyKey: string) {
  if (!target.hasOwnProperty(StateMemberSymbol)) {
    Object.defineProperty(target, StateMemberSymbol, {
      enumerable: true,
      configurable: true,
      writable: true,
    });
    target[StateMemberSymbol] = {};
  }
  if (!target[StateMemberSymbol][propertyKey]) {
    target[StateMemberSymbol] = { ...target[StateMemberSymbol], [propertyKey]: true };
  }
}

/**
 * Class decorator to define internally a method to retrieve the state of the component
 * based on flagged @StateMember property decorator
 * @param target
 */
export function WithStateMembers(target: any) {
  if (!target.prototype.StateMembers) {
    target.prototype.StateMembers = function () {
      return Object.keys(this[StateMemberSymbol] || {}).reduce(
        (acc, member) => ({ ...acc, [member]: this[member] }),
        {}
      );
    };
  }
}

/**
 * Type with `StateMembers` function
 */
export type WithStateMembersType = DataObject & { StateMembers(): DataObject };
/**
 * If componentRef.instance contains `StateMembers` function return it as WithStateMembersType type
 * else return null
 * @param componentRef
 */
export function asWithStateMemberType(componentRef: ComponentRef<any>) {
  if (isWithStateMemberCompatible(componentRef)) {
    return componentRef.instance as WithStateMembersType;
  }
  return null;
}

/**
 * Checks if passed ComponentRef instance contains `StateMembers` function
 * @param componentRef
 */
function isWithStateMemberCompatible(componentRef: ComponentRef<any>): boolean {
  return (
    componentRef &&
    componentRef.instance &&
    componentRef.instance['StateMembers'] &&
    typeof componentRef.instance['StateMembers'] === 'function'
  );
}

/**
 * Resets all @SelectionMember fields in a stored state snapshot to their empty initial value.
 * Called by the wizard on slot clear so filters are preserved but row selections are wiped.
 *
 * The initial (empty) value is derived from the prototype's own declaration:
 *   - object  → {}
 *   - array   → []
 *   - other   → null
 *
 * @param storedState  The DataObject snapshot held in wizardComponent.stepStateStore.
 * @param prototype    The component class prototype (to read SelectionMemberSymbol metadata).
 */
export function clearSelectionMembers(storedState: DataObject, prototype: any): void {
  const selectionKeys: Record<string, true> = prototype?.[SelectionMemberSymbol] ?? {};
  Object.keys(selectionKeys).forEach(key => {
    const current = storedState[key];
    if (Array.isArray(current)) {
      storedState[key] = [];
    } else if (current !== null && typeof current === 'object') {
      storedState[key] = {};
    } else {
      storedState[key] = null;
    }
  });
}
